import { Fragment, useCallback, useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { pageWide } from '@/lib/form-styles'
import { apiErrorText } from '@/lib/api-error'
import { erfuelltRecht } from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { useTerminology } from '@/lib/useTerminology'
import { useAssistentDatensatz } from '@/lib/assistent/assistent.store'
import { Reiterleiste } from '@/components/ui/Reiterleiste'
import { Calculator, ClipboardList, FileInput, FileText, Folder, LayoutDashboard, ListChecks, ListTree, NotebookPen, Receipt, Users } from 'lucide-react'
import { ProjectDataTab } from '../components/ProjectDataTab'
import { ProjectPhasesTab } from '../components/ProjectPhasesTab'
import { ProjectBillingTab } from '../components/ProjectBillingTab'
import { ProjectBillingListTab } from '../components/ProjectBillingListTab'
import { ProjectControllingTab } from '../components/ProjectControllingTab'
import { ProjectOverviewTab } from '../components/ProjectOverviewTab'
import { ProjectPositionsTab } from '../components/ProjectPositionsTab'
import { ProjectJournalTab } from '../components/ProjectJournalTab'
import { ProjectLvTab } from '../components/ProjectLvTab'
import { ProjectDeclarationsCard } from '../components/ProjectDeclarationsCard'
import { ProjectFilesPanel } from '../components/ProjectFilesPanel'
import { ProjectNavigator } from '../components/ProjectNavigator'
// AP-34 14.3: der Reiter gehört den Eingangsrechnungen — das Projekt stellt nur den Platz.
import { ProjektKosten } from '@/components/eingangsrechnungen/ProjektKosten'
import { ProjektBaustellendokumente } from '@/components/baustellendokumente/ProjektBaustellendokumente'
import { ProjektMaengel } from '@/components/baustellendokumente/ProjektMaengel'
import { ProjektOffenePunkte } from '@/components/baustellendokumente/ProjektOffenePunkte'
import { forgetRecentProject, loadRecentProjects } from '../project-navigation'
import { fetchProjectDetail } from '../api/projects-head.api'
import { PROJECT_STATUS_LABELS, PROJECT_TYPE_LABELS, alsTag, istUeberfaellig } from '../project-labels'
import type { ProjectDetail } from '../types/project.types'
import type { ProjectStatus } from '@symphenia/shared-types'

/**
 * AP-35 Detailblatt 1 5.8 — **Der Detailkopf und die Reiter.**
 *
 * Der Kopf steht auf **jedem** Reiter gleich: Nummer vor der Bezeichnung, in
 * gleicher Schrift, die Nummer nicht fett (Auftraggeber 15.9.2026); daneben Typ,
 * Status, Kunde, Betriebsstätte, Projektleitung und der Termin
 * mit Überfällig-Kennzeichen.
 *
 * Phase C liefert die Reiter **Auftragsdaten**, **Phasen & Zuständige** und
 * **Abrechnung**. Übersicht, Positionen, Journal, Dokumente, Abrechnungsliste
 * und Nachkalkulation kommen mit ihren Phasen — der Reiter steht schon hier und
 * sagt, worauf er wartet, statt zu fehlen.
 *
 * Nachtrag 5: Rechts im Kopf steht der Navigationsrahmen (Suche, Blättern,
 * zuletzt geöffnet, Neu); Status nur noch in den Projektdaten, Kopieren und die Arbeitskarte des ganzen Projekts
 * sind aus dem Kopf entfallen (die Arbeitskarte bleibt an der Position). Ohne `:id`
 * (Menüpunkt „Projekte") zeigt die Seite denselben Kopf im leeren Zustand.
 */
const REITER = [
  { key: 'overview', label: 'Übersicht', icon: LayoutDashboard },
  { key: 'data', label: 'data', icon: ClipboardList },
  { key: 'positions', label: 'positionPlural', icon: ListTree },
  // AP-35 Phase J (Blatt 12 4.3): zwischen Positionen und Journal, nur mit `.IMPORT`.
  { key: 'lv', label: 'LV', icon: FileText },
  { key: 'phases', label: 'Phasen & Zuständige', icon: Users },
  { key: 'billing', label: 'Abrechnung', icon: Receipt },
  { key: 'billinglist', label: 'Abrechnungsliste', icon: ListChecks },
  // AP-34 J: nur mit gebuchten Eingangsrechnungen und dem Recht auf deren Projektkosten.
  { key: 'incoming', label: 'Eingangsrechnungen', icon: FileInput },
  { key: 'controlling', label: 'Nachkalkulation', icon: Calculator },
  { key: 'journal', label: 'Journal & Aufgaben', icon: NotebookPen },
  { key: 'files', label: 'Dokumente', icon: Folder },
] as const

type ReiterKey = (typeof REITER)[number]['key']

export default function ProjectDetailPage() {
  const { id = '' } = useParams()
  // Absprung aus dem Cockpit (Blatt 11 4.2): `?tab=positions&position=<id>`.
  const [suche] = useSearchParams()
  const startReiter = REITER.find((r) => r.key === suche.get('tab'))?.key
  const startPosition = suche.get('position')
  // AP-37 4.15: aus der Kalkulation angelegt — der Einfügen-Dialog öffnet sich.
  const startEinfuegen = suche.get('paste') === '1'
  const terms = useTerminology()
  const { user, hasModule } = useAuthStore()
  const darf = (recht: string) => erfuelltRecht(recht, user?.permissions)
  const [projekt, setProjekt] = useState<ProjectDetail | null>(null)
  // AP-18: Der Assistent weiß, welches Projekt geöffnet ist.
  useAssistentDatensatz(projekt ? { art: 'project', id: projekt.id, bezeichnung: `${terms.project} ${projekt.projectNumber} ${projekt.name}` } : null)
  const [reiter, setReiter] = useState<ReiterKey>(startReiter ?? 'overview')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(true)

  const laden = useCallback(async () => {
    if (!id) {
      setProjekt(null)
      setLaedt(false)
      return
    }
    setLaedt(true)
    try {
      setProjekt(await fetchProjectDetail(id))
      setFehler(null)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
      // Gelöscht oder nicht mehr sichtbar — dann gehört es nicht in „zuletzt geöffnet".
      const status = (e as { response?: { status?: number } })?.response?.status
      if (status === 404 || status === 403) forgetRecentProject(id)
    } finally {
      setLaedt(false)
    }
  }, [id])

  useEffect(() => {
    void laden()
  }, [laden])

  if (!id) return <LeererKopf />
  if (laedt && !projekt) return <div className="p-6 text-sm text-muted-foreground">Lädt …</div>
  if (fehler && !projekt) {
    return <div className="p-6 text-sm text-destructive">{fehler}</div>
  }
  if (!projekt) return null

  const status = PROJECT_STATUS_LABELS[projekt.status as ProjectStatus]
  const ueberfaellig = istUeberfaellig(projekt)

  return (
    <div className={pageWide}>
      {/* ── Kopf (5.8) — kompakt (Nachtrag 2: halbe Höhe, kein „Bearbeiten";
          bearbeitet wird in den Karten der Projektdaten) ─────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-primary/30 bg-muted/20 px-5 py-4">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h1 className="truncate text-3xl font-bold text-primary">
              <span className="font-normal">{projekt.projectNumber}</span> <span>{projekt.name}</span>
            </h1>
            <span className="rounded-full border border-border px-2 py-0.5 text-xs">{PROJECT_TYPE_LABELS[projekt.type]?.(terms) ?? projekt.type}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${status?.class ?? ''}`}>{status?.label ?? projekt.status}</span>
            {projekt.isTemplate && <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">Vorlage</span>}
            {projekt.legacyNumber && <span className="text-xs text-muted-foreground">Fremdnummer {projekt.legacyNumber}</span>}
          </div>

          <dl className="mt-3 flex flex-wrap gap-x-8 gap-y-1.5 text-sm">
            <Kopfzeile label="Kunde">
              {projekt.client ? (
                <Link to={`/clients/${projekt.client.id}`} className="text-primary hover:underline">
                  {projekt.client.name}
                </Link>
              ) : (
                '—'
              )}
            </Kopfzeile>
            <Kopfzeile label="Betriebsstätte">{projekt.site?.name ?? '—'}</Kopfzeile>
            <Kopfzeile label={terms.lead}>{projekt.projectLeadName ?? '—'}</Kopfzeile>
            <Kopfzeile label="Fertigstellung bis">
              <span className={ueberfaellig ? 'font-medium text-destructive' : undefined}>
                {alsTag(projekt.completionDueAt)}
                {ueberfaellig && ' · überfällig'}
              </span>
              {projekt.completionDueNote && <span className="ml-1 text-xs text-muted-foreground">({projekt.completionDueNote})</span>}
            </Kopfzeile>
          </dl>
        </div>

        {/* Nachtrag 5: Navigationsrahmen — Suche, Blättern, zuletzt, Neu. Status wechselt in
            den Projektdaten (N5-12), Kopieren ist entfallen (N5-13). */}
        <ProjectNavigator projekt={projekt} />
      </div>

      {/* ── Reiter ──────────────────────────────────────────────────────── */}
      {/* Der Reiter der Positionen trägt den Begriff des Mandanten
          (`.claude/rules/terminologie.md`) — eine Konstante kennt ihn nicht. */}
      <Reiterleiste
        reiter={REITER.filter(
          (r) =>
            (r.key !== 'billinglist' || hasModule('PROJECT_MGMT.BILLING')) &&
            (r.key !== 'lv' || hasModule('PROJECT_MGMT.IMPORT')) &&
            (r.key !== 'incoming' || (hasModule('INCOMING_INVOICES.CORE') && darf('incoming_invoices.project_costs.read'))) &&
            (r.key !== 'controlling' || (hasModule('PROJECT_MGMT.CONTROLLING') && darf('projects.controlling.read'))),
        ).map((r) => ({ ...r, label: r.label === 'positionPlural' ? terms.positionPlural : r.label === 'data' ? terms.data : r.label }))}
        aktiv={reiter}
        onWahl={setReiter}
      />

      {fehler && <div className="text-sm text-destructive">{fehler}</div>}

      {/* Nachtrag 5: Beim Blättern bleibt die Seite montiert und der Reiter stehen —
          der Inhalt des Reiters aber beginnt je Projekt neu (offene Zeilen, Auswahl). */}
      <Fragment key={projekt.id}>
      {reiter === 'overview' && <ProjectOverviewTab projekt={projekt} />}
      {reiter === 'data' && <ProjectDataTab projekt={projekt} onChanged={laden} />}
      {reiter === 'positions' && <ProjectPositionsTab projekt={projekt} startPositionId={startPosition} startEinfuegen={startEinfuegen} />}
      {reiter === 'lv' && hasModule('PROJECT_MGMT.IMPORT') && <ProjectLvTab projekt={projekt} />}
      {reiter === 'phases' && <ProjectPhasesTab projekt={projekt} />}
      {reiter === 'billing' && <ProjectBillingTab projekt={projekt} onChanged={laden} />}
      {reiter === 'billinglist' && <ProjectBillingListTab projectId={projekt.id} onChanged={() => void laden()} />}
      {reiter === 'incoming' && hasModule('INCOMING_INVOICES.CORE') && darf('incoming_invoices.project_costs.read') && <ProjektKosten projectId={projekt.id} />}
      {reiter === 'controlling' && <ProjectControllingTab projectId={projekt.id} />}
      {reiter === 'journal' && <ProjectJournalTab projekt={projekt} />}
      {reiter === 'files' && (
        <div className="space-y-4">
          <ProjectFilesPanel projectId={projekt.id} entityType="PROJECT" entityId={projekt.id} />
          <ProjectDeclarationsCard projectId={projekt.id} />
          {/* AP-46 10.4: die Baustellendokumente des Projekts — nur, wenn der Baustein gebucht ist. */}
          {hasModule('SITE_DOCS.CORE') && darf('sitedocs.document.read') && <ProjektBaustellendokumente projectId={projekt.id} />}
          {/* AP-47 6.2: offene Mängel der Montageprotokolle. */}
          {hasModule('SITE_DOCS.ASSEMBLY') && darf('sitedocs.document.read') && <ProjektMaengel projectId={projekt.id} />}
          {/* AP-49 8.: offene Punkte aus Besprechungen — nur mit dem Baustein Projektbesprechung. */}
          {hasModule('SITE_DOCS.MEETING') && darf('sitedocs.document.read') && <ProjektOffenePunkte projectId={projekt.id} />}
        </div>
      )}
      </Fragment>

    </div>
  )
}

function Kopfzeile({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="shrink-0 text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="font-medium text-foreground">{children}</dd>
    </div>
  )
}

/**
 * Nachtrag 5 — der Menüpunkt „Projekte" ohne gewähltes Projekt: derselbe Kopf,
 * links „Kein Projekt gewählt" mit leeren Kopfdaten, rechts der Rahmen mit
 * hervorgehobenem „Neu". Darunter die zuletzt geöffneten als Absprung.
 */
function LeererKopf() {
  const terms = useTerminology()
  const zuletzt = loadRecentProjects().slice(0, 6)
  return (
    <div className={pageWide}>
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-primary/30 bg-muted/20 px-5 py-4">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h1 className="truncate text-3xl font-normal text-muted-foreground">{terms.none} gewählt</h1>
          </div>
          <dl className="mt-3 flex flex-wrap gap-x-8 gap-y-1.5 text-sm">
            <Kopfzeile label="Kunde">—</Kopfzeile>
            <Kopfzeile label="Betriebsstätte">—</Kopfzeile>
            <Kopfzeile label={terms.lead}>—</Kopfzeile>
            <Kopfzeile label="Fertigstellung bis">—</Kopfzeile>
          </dl>
        </div>
        <ProjectNavigator projekt={null} />
      </div>

      <div className="rounded-lg border border-dashed border-border px-5 py-4 text-sm text-muted-foreground">
        <p>
          Oben rechts suchen, mit ‹ › durch die {terms.list} blättern oder mit „Neu“ anlegen.
        </p>
        {zuletzt.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-wide">Zuletzt geöffnet</span>
            {zuletzt.map((p) => (
              <Link
                key={p.id}
                to={`/projects/${p.id}`}
                className="rounded-md border border-border bg-background px-2.5 py-1 text-foreground hover:border-primary/50"
              >
                <span className="text-muted-foreground">{p.projectNumber}</span> {p.name}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
